import { useState, useRef, useCallback, useEffect } from "react";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const buildGradient = (amberStart, greenStart) => {
  return [
    `#e84444 0%`,
    `#f0a030 ${amberStart}%`,
    `#e8c844 ${(amberStart + greenStart) / 2}%`,
    `#44c464 ${greenStart}%`,
    `#44c464 100%`,
  ].join(", ");
};

const getZoneColor = (valuePct, amberStart, greenStart) => {
  if (valuePct < amberStart)  return "#e84444";
  if (valuePct < greenStart)  return "#f0a030";
  return "#44c464";
};

const StartHandle = ({ pct, onMouseDown }) => (
  <div
    onMouseDown={onMouseDown}
    onTouchStart={onMouseDown}
    title="Drag to adjust starting point"
    style={{
      position: "absolute",
      left: `${pct}%`,
      top: 0, bottom: 0,
      transform: "translateX(-50%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "ew-resize",
      zIndex: 15,
      width: 20,
    }}
  >
    <div style={{
      width: 2.5,
      height: "100%",
      background: "rgba(255,255,255,0.65)",
      borderRadius: 2,
    }} />
  </div>
);



// ─── Reusable GradientSlider ──────────────────────────────────────────────────
// Props:
//   min          {number}   Minimum value of the range
//   max          {number}   Maximum value of the range
//   value        {number}   Current value
//   amberStart   {number}   % position (0–100) where amber zone starts
//   greenStart   {number}   % position (0–100) where green zone starts
//   onChange     {fn}       ({ value, amberStart, greenStart }) => void
// ─────────────────────────────────────────────────────────────────────────────
export const GradientSlider = ({
  min = 0,
  max = 100,
  value = 50,
  amberStart = 25,
  greenStart = 75,
  onChange,
}) => {
  const trackRef = useRef(null);
  const dragging = useRef(null);

  const hasValue  = value !== null && value !== undefined && value !== '-' && !isNaN(Number(value));
  const valuePct  = hasValue ? clamp(((value - min) / (max - min)) * 100, 0, 100) : 0;
  const zoneColor = hasValue ? getZoneColor(valuePct, amberStart, greenStart) : "#aaaaaa";

  const clientXToPct = useCallback((clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
  }, []);

  const pctToValue = useCallback((pct) => {
    return Math.round(min + (pct / 100) * (max - min));
  }, [min, max]);

  const handleTrackMouseDown = useCallback((e) => {
    dragging.current = "value";
    const pct = clientXToPct(e.clientX);
    if (pct !== null) onChange?.({ value: pctToValue(pct), amberStart, greenStart });
  }, [clientXToPct, pctToValue, amberStart, greenStart, onChange]);

  const handleStartMouseDown = useCallback((e, which) => {
    e.stopPropagation();
    dragging.current = which;
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const pct = clientXToPct(clientX);
      if (pct === null) return;
      const rounded = Math.round(pct);
      if (dragging.current === "value") {
        onChange?.({ value: pctToValue(pct), amberStart, greenStart });
      } else if (dragging.current === "amber") {
        onChange?.({ value, amberStart: clamp(rounded, 0, greenStart - 2), greenStart });
      } else if (dragging.current === "green") {
        onChange?.({ value, amberStart, greenStart: clamp(rounded, amberStart + 2, 100) });
      }
    };
    const onUp = () => { dragging.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [clientXToPct, pctToValue, value, amberStart, greenStart, onChange]);

  return (
    <div style={{ position: "relative", fontFamily: "'DM Sans', sans-serif" }}>

      {/* Track */}
      <div
        ref={trackRef}
        onMouseDown={handleTrackMouseDown}
        style={{
          position: "relative",
          height: 15,
          borderRadius: 999,
          background: `linear-gradient(to right, ${buildGradient(amberStart, greenStart)})`,
          cursor: "crosshair",
          userSelect: "none",
          boxShadow: "0 2px 12px rgba(0,0,0,0.1), inset 0 1px 3px rgba(255,255,255,0.2)",
        }}
      >
        {/* Dim overlay after thumb */}
        <div style={{
          position: "absolute",
          left: `${valuePct}%`,
          right: 0,
          top: 0, bottom: 0,
          background: "rgba(255,255,255,0.65)",
          borderRadius: "0 999px 999px 0",
          pointerEvents: "none",
          zIndex: 5,
        }} />

        {/* Amber starting point handle */}
        <StartHandle
          pct={amberStart}
          onMouseDown={(e) => handleStartMouseDown(e, "amber")}
        />

        {/* Green starting point handle */}
        <StartHandle
          pct={greenStart}
          onMouseDown={(e) => handleStartMouseDown(e, "green")}
        />

        {/* Value pointer */}
        <div style={{
          position: "absolute",
          left: `${valuePct}%`,
          top: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
          zIndex: 20,
          width: 18, height: 18,
        }}>
          <span style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginBottom: 3,
            fontSize: 11, fontWeight: 700,
            color: zoneColor,
            whiteSpace: "nowrap",
            lineHeight: 1,
          }}>
            {hasValue ? `${value}%` : '-'}
          </span>
          <div style={{
            width: 18, height: 18, borderRadius: "50%",
            background: zoneColor,
            boxShadow: "0 1px 6px rgba(0,0,0,0.45), 0 0 0 2.5px #fff",
          }} />
        </div>
      </div>

      {/* Min / start / max labels below track */}
      <div style={{ position: "relative", height: 22, marginTop: 10 }}>
        <span style={{ position: "absolute", left: 0, fontSize: 11, fontWeight: 600, color: "#bbb" }}>0%</span>
        <span style={{
          position: "absolute", left: `${amberStart}%`,
          transform: "translateX(-50%)",
          fontSize: 11, fontWeight: 700, color: "#f0a030", whiteSpace: "nowrap", opacity: 0.55
        }}>{amberStart}%</span>
        <span style={{
          position: "absolute", left: `${greenStart}%`,
          transform: "translateX(-50%)",
          fontSize: 11, fontWeight: 700, color: "#44c464", whiteSpace: "nowrap", opacity: 0.55
        }}>{greenStart}%</span>
        <span style={{ position: "absolute", right: 0, fontSize: 11, fontWeight: 600, color: "#bbb" }}>100%</span>
      </div>

    </div>
  );
};
